import { router, usePage } from "@inertiajs/react"
import { Check, ChevronsUpDown } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { BrandLogo } from "@/components/brand-logo"
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { useCurrentContext } from "@/hooks/use-current-context"
import { cn } from "@/lib/utils"
import { currentBrandPath } from "@/routes"
import type { CurrentBrand } from "@/types"

interface BrandSwitcherProps {
  className?: string
}

/**
 * BrandSwitcher — agency multi-brand context switcher.
 *
 * Visibility (CTPO contract): only when
 *   `currentUser.role === "agency"` AND `availableBrands.length > 1`.
 * Caller is expected to enforce this; component returns `null` defensively.
 *
 * Composition: shadcn `CommandDialog` (Dialog + Command correctly wired with
 * `data-slot="command-input-wrapper"` sizing so the search input never
 * collapses). Global ⌘K / Ctrl-K shortcut. The visible trigger is a plain
 * button we control — CommandDialog doesn't ship a trigger slot by design.
 *
 * Selecting a brand POSTs to `currentBrandPath()` and triggers a full reload
 * (`preserveState: false`) so server-side tenancy props rewrite cleanly.
 *
 * # OKS-854 — the forced second visit is gone
 *
 * This handler used to fire an extra `router.visit(window.location.href)`
 * in `onSuccess`. That existed because the switch 303'd to
 * `dashboard_path`, which itself 303'd to the role home: Inertia followed
 * the CHAIN, and deferred-prop resolution at the final landing could dedupe
 * and leave the previous brand's tree rendered. The agency switch now
 * redirects ONCE, straight to the referer-derived destination, so the chain
 * that caused it is gone and the extra round-trip only raced the arrival
 * splash.
 *
 * ⚠️ "The chain is gone" is not a general invariant, though — a destination
 * that self-canonicalizes rebuilds one (e.g. bare `/candidates` 303s to
 * `?country=FR`). If stale deferred props ever resurface, the fix is NOT to
 * restore the blanket second visit but to `router.reload({only: [...]})` on
 * the affected destination. The `/candidates` switch E2E exists to catch
 * exactly this.
 *
 * NB: an `asMenuItem` variant for the <768px breakpoint (switcher nested inside
 * NavUser dropdown) was explored but is not wired in OKS-5 — tracked as a
 * follow-up. Kept out of the API to avoid dead-code drift.
 */
export function BrandSwitcher({ className }: BrandSwitcherProps) {
  const { currentUser, currentBrand, availableBrands } = useCurrentContext()
  const { t } = useTranslation()
  const { flash } = usePage()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  // Only render for agencies with ≥2 brand assignments.
  const isVisible = currentUser?.role === "agency" && availableBrands.length > 1

  // OKS-854 (UX Spec §6 / UQ-3) — after a switch, focus returns to the
  // trigger, because the trigger's LABEL is what changed. Without this a
  // keyboard user is dropped at the top of a freshly re-rendered page with
  // no announcement of the new scope.
  //
  // Why the flash and not `onSuccess`: the switch posts with
  // `preserveState: false`, so the whole tree — this component included —
  // is REPLACED. A ref captured before the visit is stale by the time the
  // response lands, so the restore has to happen on the NEW mount. The
  // server's one-shot `brand_switch_splash` flash is exactly the
  // "a switch just arrived" signal, and it is already the thing that
  // distinguishes a switch from a plain reload (PRD §3: a reload is not a
  // switch), so a reload correctly restores no focus.
  //
  // ⚠️ Coupling worth knowing: this rides the SAME flash that drives the
  // arrival splash. If the splash is ever removed from the agency branch,
  // move this to its own signal rather than letting focus restore die
  // silently with it. Focusing under the splash is safe — the overlay is
  // decorative and `aria-hidden`, and never traps focus.
  const arrivedFromSwitch = Boolean(flash?.brand_switch_splash)

  useEffect(() => {
    if (!isVisible || !arrivedFromSwitch) return
    triggerRef.current?.focus()
  }, [isVisible, arrivedFromSwitch])

  useEffect(() => {
    if (!isVisible) return
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        setOpen((prev) => !prev)
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => {
      window.removeEventListener("keydown", onKeyDown)
    }
  }, [isVisible])

  if (!isVisible) return null

  function handleSelect(brand: CurrentBrand) {
    if (currentBrand?.id === brand.id) {
      setOpen(false)
      return
    }
    setOpen(false)
    router.post(
      currentBrandPath(),
      { brand_id: brand.id },
      {
        // `preserveState: false` forces the ENTIRE prop tree — including
        // deferred props — to re-resolve under the new `Current.brand`.
        // This is what keeps the destination page from rendering the
        // previous brand's data, and it is load-bearing: see below.
        preserveScroll: false,
        preserveState: false,
        onError: () => {
          toast.error(t("brand_switcher.error_toast"))
        },
      },
    )
  }

  const triggerLabel = t("brand_switcher.trigger_label", {
    name: currentBrand?.name ?? "",
  })

  return (
    <>
      {/*
        Desktop trigger shows the current brand name so sighted keyboard users
        have a visible affordance for what the control switches. ⌘K stays as
        a supplementary hint on the right.
       */}
      <button
        ref={triggerRef}
        type="button"
        data-testid="brand-switcher-trigger"
        aria-label={triggerLabel}
        onClick={() => {
          setOpen(true)
        }}
        className={cn(
          // OKS-854 (UX Spec §5) — ≥44px touch target at every width. Was a
          // hard `h-9` (36px), which failed the mobile floor. `min-h-11` +
          // `py-2` mirrors the sibling `CrewSwitcher` trigger rather than
          // inventing a breakpoint-scoped variant; the shell header is
          // `h-16`, so 44px fits at all widths and the two switchers stay
          // visually consistent.
          "border-input hover:bg-accent focus-visible:ring-ring flex min-h-11 min-w-0 items-center gap-2 rounded-md border bg-transparent px-3 py-2 text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none",
          className,
        )}
      >
        <span className="max-w-[14ch] truncate font-medium sm:max-w-[20ch]">
          {currentBrand?.name ?? t("brand_switcher.group_heading")}
        </span>
        <ChevronsUpDown
          className="size-4 shrink-0 opacity-60"
          aria-hidden="true"
        />
        <span
          className="text-muted-foreground ml-1 hidden md:inline"
          aria-hidden="true"
        >
          ⌘K
        </span>
      </button>
      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        title={t("brand_switcher.dialog_title")}
        description={t("brand_switcher.dialog_description")}
      >
        <CommandInput placeholder={t("brand_switcher.search_placeholder")} />
        <CommandList>
          <CommandEmpty>{t("brand_switcher.empty")}</CommandEmpty>
          <CommandGroup heading={t("brand_switcher.group_heading")}>
            {availableBrands.map((brand) => {
              const isCurrent = currentBrand?.id === brand.id
              return (
                <CommandItem
                  key={brand.id}
                  value={`${brand.name} ${brand.slug}`}
                  onSelect={() => {
                    handleSelect(brand)
                  }}
                  data-testid={`brand-switcher-item-${brand.id}`}
                >
                  <BrandAvatar brand={brand} />
                  <span className="truncate">{brand.name}</span>
                  {isCurrent && (
                    <Check
                      aria-label={t("brand_switcher.current_indicator")}
                      className="ml-auto size-4"
                    />
                  )}
                </CommandItem>
              )
            })}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  )
}

/**
 * BrandAvatar — chrome for a single brand row in the switcher list.
 *
 * OKS-316 — Migrated from a bespoke `<img>` / monogram `<div>` block
 * onto the shared `<BrandLogo>` wrapper (OKS-303 single source of
 * truth for brand chrome). Size + shape preserved verbatim via the
 * `className` prop; `alt=""` because the adjacent brand name span is
 * the textual identity. The `testId` emits the container + `-image`
 * / `-fallback` children per the OKS-303 KD-3 contract.
 */
function BrandAvatar({ brand }: { brand: CurrentBrand }) {
  return (
    <BrandLogo
      logoUrl={brand.logo_url}
      name={brand.name}
      className="size-6 shrink-0 rounded-full"
      testId="brand-switcher-logo"
    />
  )
}
